import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Link, CurrencyDollar, Users, Wallet } from '@phosphor-icons/react';

export default function ReferralDashboard() {
  const { user, token } = useAuth();
  const [stats, setStats] = useState(null);
  const [earnings, setEarnings] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadStats();
    loadEarnings();
    loadTransactions();
  }, []);

  const loadStats = async () => {
    try {
      const response = await api.get('/api/referrals/stats', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const loadEarnings = async () => {
    try {
      const response = await api.get('/api/referrals/earnings', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEarnings(response.data);
    } catch (error) {
      console.error('Failed to load earnings:', error);
    }
  };

  const loadTransactions = async () => {
    try {
      const response = await api.get('/api/wallet/transactions', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTransactions(response.data);
    } catch (error) {
      console.error('Failed to load transactions:', error);
    }
  };

  const handleWithdraw = async () => {
    const amount = parseFloat(withdrawAmount);
    if (amount < 10) {
      toast.error('Minimum withdrawal is $10');
      return;
    }
    
    try {
      setLoading(true);
      await api.post('/api/wallet/withdraw', { amount }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Withdrawal request submitted');
      setWithdrawAmount('');
      loadStats();
      loadTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Withdrawal failed');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  if (!stats) return <div className="min-h-screen bg-[#050505] flex items-center justify-center text-zinc-400">Loading...</div>;

  return (
    <div className="min-h-screen bg-[#050505] py-12">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <h1 className="text-3xl font-serif text-white mb-8">Referral Dashboard</h1>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-[#121212] border border-white/10 p-6">
            <div className="flex items-center gap-3 mb-2">
              <Users size={24} className="text-[#D4AF37]" weight="duotone" />
              <p className="text-zinc-500 text-sm">Total Referrals</p>
            </div>
            <p className="text-3xl font-serif text-white">{stats.total_referrals}</p>
          </div>
          
          <div className="bg-[#121212] border border-white/10 p-6">
            <div className="flex items-center gap-3 mb-2">
              <CurrencyDollar size={24} className="text-[#D4AF37]" weight="duotone" />
              <p className="text-zinc-500 text-sm">Total Earnings</p>
            </div>
            <p className="text-3xl font-serif text-white">${stats.total_earnings.toFixed(2)}</p>
          </div>
          
          <div className="bg-[#121212] border border-white/10 p-6">
            <div className="flex items-center gap-3 mb-2">
              <Wallet size={24} className="text-[#D4AF37]" weight="duotone" />
              <p className="text-zinc-500 text-sm">Withdrawable Balance</p>
            </div>
            <p className="text-3xl font-serif text-white">${stats.withdrawable_balance.toFixed(2)}</p>
          </div>
        </div>

        {/* Referral Link */}
        <div className="bg-[#121212] border border-white/10 p-6 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Link size={24} className="text-[#D4AF37]" weight="duotone" />
            <h3 className="text-xl font-serif text-white">Your Referral Link</h3>
          </div>
          
          <div className="flex gap-3 mb-3">
            <Input
              value={stats.referral_link}
              readOnly
              className="bg-[#0A0A0A] border-white/20 text-white"
            />
            <Button
              onClick={() => copyToClipboard(stats.referral_link)}
              className="bg-[#D4AF37] hover:bg-[#B5952F] text-black font-semibold"
            >
              Copy
            </Button>
          </div>
          
          <div className="flex items-center gap-3">
            <p className="text-zinc-500 text-sm">Referral Code:</p>
            <code className="bg-[#0A0A0A] border border-white/20 text-[#D4AF37] px-3 py-1 font-mono">
              {stats.referral_code}
            </code>
            <Button
              onClick={() => copyToClipboard(stats.referral_code)}
              className="bg-transparent border border-white/20 text-white hover:bg-white/5 text-sm"
            >
              Copy
            </Button>
          </div>
          
          <p className="text-zinc-500 text-sm mt-4">
            💰 Earn $0.50 for every transaction your referrals make (booking or hosting) - forever!
          </p>
        </div>

        {/* Withdrawal */}
        {stats.withdrawable_balance >= 10 && (
          <div className="bg-[#121212] border border-white/10 p-6 mb-8">
            <h3 className="text-xl font-serif text-white mb-4">Request Withdrawal</h3>
            <div className="flex gap-3">
              <Input
                type="number"
                placeholder="Amount ($10 minimum)"
                value={withdrawAmount}
                onChange={(e) => setWithdrawAmount(e.target.value)}
                className="bg-transparent border-white/20 text-white"
              />
              <Button
                onClick={handleWithdraw}
                disabled={loading || parseFloat(withdrawAmount) < 10}
                className="bg-[#D4AF37] hover:bg-[#B5952F] text-black font-semibold"
              >
                Withdraw
              </Button>
            </div>
          </div>
        )}

        {/* Recent Earnings */}
        <div className="bg-[#121212] border border-white/10 p-6 mb-8">
          <h3 className="text-xl font-serif text-white mb-4">Recent Earnings</h3>
          {earnings.length === 0 ? (
            <p className="text-zinc-500">No earnings yet. Start referring!</p>
          ) : (
            <div className="space-y-3">
              {earnings.slice(0, 10).map((earning) => (
                <div key={earning.id} className="flex items-center justify-between py-3 border-b border-white/10 last:border-0">
                  <div>
                    <p className="text-white">${earning.commission_amount.toFixed(2)}</p>
                    <p className="text-sm text-zinc-500">{earning.transaction_type.replace('_', ' ')}</p>
                  </div>
                  <p className="text-xs text-zinc-600">
                    {new Date(earning.created_at).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Wallet Transactions */}
        <div className="bg-[#121212] border border-white/10 p-6">
          <h3 className="text-xl font-serif text-white mb-4">Wallet History</h3>
          {transactions.length === 0 ? (
            <p className="text-zinc-500">No transactions yet</p>
          ) : (
            <div className="space-y-3">
              {transactions.map((tx) => (
                <div key={tx.id} className="flex items-center justify-between py-3 border-b border-white/10 last:border-0">
                  <div>
                    <p className={tx.amount > 0 ? 'text-green-500' : 'text-red-500'}>
                      {tx.amount > 0 ? '+' : ''}
                      ${Math.abs(tx.amount).toFixed(2)}
                    </p>
                    <p className="text-sm text-zinc-500">{tx.description}</p>
                  </div>
                  <p className="text-xs text-zinc-600">
                    {new Date(tx.created_at).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}